import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeftRight } from 'lucide-react';
import { z } from 'zod';
import { Form } from '@/components/ui/form';
import { PCMP_CHANNEL_LABELS } from '../../constants/pcmpProposal.constants';
import { useChangePcmpPaymentSource } from '../../hooks/useChangePcmpPaymentSource';
import type { PcmpChannel, PcmpProposalDetail } from '../../types/pcmpProposal.types';
import { pcmpEditReasonSchema } from '../../utils/pcmpProposalEditForm';
import { PcmpEditDialog } from './PcmpEditDialog';
import { PcmpEditReasonField } from './PcmpEditReasonField';

const paymentSourceFormSchema = z.object({ reason: pcmpEditReasonSchema });

type PaymentSourceFormValues = z.infer<typeof paymentSourceFormSchema>;

interface PcmpPaymentSourceDialogProps {
  proposal: PcmpProposalDetail;
  target: PcmpChannel;
  onClose: () => void;
}

/** Legacy UpdatePaymentSource; the API also asks for a reason. */
export function PcmpPaymentSourceDialog({
  proposal,
  target,
  onClose,
}: PcmpPaymentSourceDialogProps) {
  const form = useForm<PaymentSourceFormValues>({
    resolver: zodResolver(paymentSourceFormSchema),
    defaultValues: { reason: '' },
  });
  const { mutate, isPending, error } = useChangePcmpPaymentSource(proposal.proposalId, onClose);

  const onSubmit = form.handleSubmit((values) =>
    mutate({ paymentSource: target, reason: values.reason }),
  );

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title="Change Payment Source"
        icon={ArrowLeftRight}
        capabilities={proposal.capabilities}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel={`Switch to ${PCMP_CHANNEL_LABELS[target]}`}
      >
        <p className="text-sm">
          Change the payment source of {proposal.refNo} from{' '}
          <span className="font-semibold">{PCMP_CHANNEL_LABELS[proposal.paymentSource]}</span> to{' '}
          <span className="font-semibold">{PCMP_CHANNEL_LABELS[target]}</span>.
        </p>
        <PcmpEditReasonField control={form.control} />
      </PcmpEditDialog>
    </Form>
  );
}
